import { CopyableEntry } from '@dittolive/anvil'
import dayjs from 'dayjs'
import React, { useCallback } from 'react'

import type {
  LiveQueryKafkaDestination,
  LiveQuerySinkConfig,
} from '../../../types/dataBridge'
import { isKafkaSinkConfig } from '../../../types/dataBridge'
import DataBridgeSectionRowContainer from '../components/DataBridgeSectionRowContainer'
import { defaultStringActionRenderer } from '../components/DataBridgeStringFieldCopyAction'
import GenericKeyValue from '../components/GenericKeyValue'
import KafkaCertCard from './KafkaCertCard'
import { extractExpirationFromCert } from './utils'

type Props = {
  destination: LiveQueryKafkaDestination
  configuration: LiveQuerySinkConfig | null
  /**
   * Optional callback when certificate extraction fails
   */
  onCertExtractionError?: (error: Error) => void
}

export default function KafkaDestination({
  destination: { kafkaConsumer },
  configuration,
  onCertExtractionError,
}: Props) {
  const renderConfig = useCallback(() => {
    if (!configuration || !isKafkaSinkConfig(configuration)) {
      return null
    }

    const extractCertExpiration = (certBase64: string, password: string) => {
      try {
        const date = extractExpirationFromCert(certBase64, password)
        if (!date) return null
        return dayjs(date)
      } catch (e) {
        if (onCertExtractionError && e instanceof Error) {
          onCertExtractionError(e)
        }
        return null
      }
    }

    const { endpoint, authConfig } = configuration.kafka
    const endpointWithPort = endpoint.match(/:\d+$/)
      ? endpoint
      : endpoint + ':443'

    const clusterCertPassword = Buffer.from(
      authConfig.clusterCaCertPassword,
      'base64',
    ).toString()
    const userCertPassword = Buffer.from(
      authConfig.userCaCertPassword,
      'base64',
    ).toString()

    const passwords = [
      {
        title: 'Cluster certificate password',
        value: clusterCertPassword,
      },
      {
        title: 'User certificate password',
        value: userCertPassword,
      },
    ]

    return (
      <>
        <DataBridgeSectionRowContainer data-testid="endpoint">
          <CopyableEntry
            value={endpointWithPort}
            title="Endpoint"
            isCode
            fullWidth
          />
        </DataBridgeSectionRowContainer>

        {authConfig.clusterCaCert && authConfig.userCaCert && (
          <div
            className="flex w-full items-center gap-3 p-4"
            data-testid="kafkaCerts"
          >
            <KafkaCertCard
              cert={authConfig.clusterCaCert}
              title="Cluster certificate"
              filename="cluster.p12"
              expiration={extractCertExpiration(
                authConfig.clusterCaCert,
                clusterCertPassword,
              )}
            />

            <KafkaCertCard
              cert={authConfig.userCaCert}
              title="User certificate"
              filename="user.p12"
              expiration={extractCertExpiration(
                authConfig.userCaCert,
                userCertPassword,
              )}
            />
          </div>
        )}

        {passwords.map(
          ({ title, value }) =>
            value && (
              <DataBridgeSectionRowContainer
                key={title}
                data-testid="kafkaCertPassword"
              >
                <CopyableEntry
                  title={title}
                  value={value}
                  mask
                  isCode
                  fullWidth
                />
              </DataBridgeSectionRowContainer>
            ),
        )}
      </>
    )
  }, [configuration, onCertExtractionError])

  return (
    <div>
      <GenericKeyValue
        label="Topic"
        value={kafkaConsumer.topicName}
        actionRenderer={() =>
          defaultStringActionRenderer(kafkaConsumer.topicName)
        }
      />

      <GenericKeyValue
        label="Consumer group prefix"
        value={kafkaConsumer.consumerGroup}
        actionRenderer={() =>
          defaultStringActionRenderer(kafkaConsumer.consumerGroup)
        }
      />

      {renderConfig()}
    </div>
  )
}
